import React, { useState, useRef } from "react";
import Plot from "react-plotly.js";
import MDBox from "../../controls/MDBox";
import MDTypography from "../../controls/MDTypography";
import MDButton from "../../controls/MDButton";
import { Autocomplete, TextField } from "@mui/material";
import { FileExcel } from 'react-bootstrap-icons';
import { Download } from "@mui/icons-material";
import ExcelJS from "exceljs";
import html2canvas from "html2canvas";

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1', '#d084d0', '#82d084', '#a4de6c', '#d0ed57', '#83a6ed'];

const darken = (hex, amt = 55) => {
  try {
    let c = hex.replace('#', '');
    if (c.length === 3) c = c.split('').map(ch => ch + ch).join('');
    const n = parseInt(c, 16);
    const clamp = (v) => Math.min(255, Math.max(0, v));
    const r = clamp(((n >> 16) & 0xff) - amt);
    const g = clamp(((n >> 8) & 0xff) - amt);
    const b = clamp((n & 0xff) - amt);
    const toHex = (v) => v.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  } catch {
    return '#444';
  }
};

const BarChartStackedCustom = ({ data, title, observaciones }) => {
  const chartRef = useRef(null);

  // Extraer opciones únicas para los filtros
  const organismosUnicos = [...new Set(data.map(item => item.label2))].sort();

  // Estados para los filtros
  const [selectedOrganismo, setSelectedOrganismo] = useState(null);
  const [hiddenEstados, setHiddenEstados] = useState([]);

  // Función para dividir texto largo en dos líneas
  const formatLabel = (text) => {
    if (!text || text.length <= 14) return text;
    const mid = Math.floor(text.length / 2);
    const spaceIndex = text.indexOf(' ', mid - 7);
    if (spaceIndex > 0 && spaceIndex < mid + 7) {
      return text.substring(0, spaceIndex) + '<br>' + text.substring(spaceIndex + 1);
    }
    return text.substring(0, mid) + '<br>' + text.substring(mid);
  };

  // Filtrar datos según el filtro de organismo
  const filteredData = data.filter(item => {
    const passOrganismo = !selectedOrganismo || item.label2 === selectedOrganismo;
    return passOrganismo;
  });

  // Obtener usuarios únicos del conjunto filtrado
  const usuariosUnicos = [...new Set(filteredData.map(item => item.label3))].sort().map(formatLabel);

  // Obtener todos los estados únicos
  const estadosUnicos = [...new Set(filteredData.map(item => item.label1))];

  // Crear estructura de datos: por cada estado, obtener valores para cada usuario
  const plotData = estadosUnicos
    .filter(estado => !hiddenEstados.includes(estado))
    .map((estado, index) => {
      // Para cada usuario, buscar el valor de este estado
      const valores = usuariosUnicos.map(usuario => {
        const item = filteredData.find(d => d.label3 === usuario && d.label1 === estado);
        return item ? item.valor1 : 0;
      });

      return {
        type: 'scatter',
        mode: 'lines+markers',
        name: estado,
        x: usuariosUnicos,
        y: valores,
        line: {
          color: COLORS[index % COLORS.length],
          width: 2.5
        },
        marker: {
          size: 14,
          color: COLORS[index % COLORS.length],
          line: {
            color: darken(COLORS[index % COLORS.length], 60),
            width: 2.5
          },
          symbol: 'diamond',
          opacity: 0.9
        },
        hovertemplate: '<b>%{x}</b><br>' +
                       estado + ': %{y}<br>' +
                       '<extra></extra>',
      };
    });

  const layout = {
    title: {
      text: '',
      font: { size: 18, color: '#344767' }
    },
    xaxis: {
      title: data[0]?.label3Nombre || 'Usuario',
      tickangle: -45,
      tickfont: {
        size: 10
      },
      automargin: true,
      range: [-0.5, usuariosUnicos.length - 0.5]
    },
    yaxis: {
      title: data[0]?.valor1Nombre || 'Cantidad',
      rangemode: 'tozero'
    },
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: 'rgba(0,0,0,0)',
    showlegend: true,
    legend: {
      orientation: 'v',
      x: 1.05,
      xanchor: 'left',
      y: 1,
      yanchor: 'top'
    },
    margin: { l: 60, r: 250, t: 40, b: 150 },
    height: 600,
  };

  const config = {
    displayModeBar: false,
    displaylogo: false,
  };

  const handleLegendClick = (estadoName) => {
    setHiddenEstados((prev) => {
      if (prev.includes(estadoName)) {
        return prev.filter((name) => name !== estadoName);
      } else {
        return [...prev, estadoName];
      }
    });
  };

  const handleSelectAll = () => {
    setHiddenEstados([]);
  };

  const handleSelectNone = () => {
    setHiddenEstados(estadosUnicos);
  };

  const exportToExcel = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Datos");

    // Headers
    const headers = ['Estado', 'Organismo', 'Usuario', 'Cantidad'];
    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "1976d2" }
      };
      cell.font = { color: { argb: "FFFFFF" }, bold: true };
    });

    // Agregar datos filtrados
    filteredData.forEach((item) => {
      const row = [item.label1, item.label2, item.label3, item.valor1];
      worksheet.addRow(row);
    });

    // Agregar resumen por usuario
    worksheet.addRow([]);
    const summaryHeaderRow = worksheet.addRow(['Usuario', ...estadosUnicos, 'Total']);
    summaryHeaderRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "4CAF50" }
      };
      cell.font = { color: { argb: "FFFFFF" }, bold: true };
    });

    usuariosUnicos.forEach((usuario) => {
      const valores = estadosUnicos.map(estado => {
        const item = filteredData.find(d => d.label3 === usuario && d.label1 === estado);
        return item ? item.valor1 : 0;
      });
      const total = valores.reduce((sum, val) => sum + val, 0);
      worksheet.addRow([usuario, ...valores, total]);
    });

    // Auto-ajustar columnas
    worksheet.columns.forEach((column) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const columnLength = cell.value ? cell.value.toString().length : 10;
        if (columnLength > maxLength) {
          maxLength = columnLength;
        }
      });
      column.width = maxLength < 10 ? 10 : maxLength + 2;
    });

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${title || 'grafico_apilado'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(link);
    });
  };

  const handleDownloadPNG = async () => {
    if (chartRef.current) {
      try {
        const canvas = await html2canvas(chartRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
        });
        const link = document.createElement('a');
        link.download = `${title || 'grafico_apilado'}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      } catch (error) {
        console.error('Error al descargar el gráfico:', error);
      }
    }
  };

  const renderLegend = () => {
    return (
      <MDBox>
        <MDBox style={{ display: 'flex', gap: '8px', justifyContent: 'center', paddingTop: '10px', paddingBottom: '8px' }}>
          <MDBox
            onClick={handleSelectAll}
            style={{
              cursor: 'pointer',
              padding: '4px 12px',
              backgroundColor: '#e3f2fd',
              borderRadius: '4px',
              fontSize: '0.7rem',
              color: '#1976d2',
              fontWeight: '500',
            }}
          >
            Mostrar todos
          </MDBox>
          <MDBox
            onClick={handleSelectNone}
            style={{
              cursor: 'pointer',
              padding: '4px 12px',
              backgroundColor: '#fce4ec',
              borderRadius: '4px',
              fontSize: '0.7rem',
              color: '#c2185b',
              fontWeight: '500',
            }}
          >
            Ocultar todos
          </MDBox>
        </MDBox>
        <MDBox style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center', paddingTop: '10px' }}>
          {estadosUnicos.map((estado, index) => {
            const total = filteredData
              .filter(d => d.label1 === estado)
              .reduce((sum, d) => sum + d.valor1, 0);
            
            return (
              <MDBox
                key={`legend-${index}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  opacity: hiddenEstados.includes(estado) ? 0.4 : 1,
                  transition: 'opacity 0.3s',
                }}
                onClick={() => handleLegendClick(estado)}
              >
                <MDBox
                  style={{
                    width: '10px',
                    height: '10px',
                    backgroundColor: COLORS[index % COLORS.length],
                    marginRight: '6px',
                    borderRadius: '2px',
                  }}
                />
                <MDTypography variant="caption" fontSize="0.7rem" color="text">
                  {estado} ({total})
                </MDTypography>
              </MDBox>
            );
          })}
        </MDBox>
      </MDBox>
    );
  };

  if (!data || data.length === 0) {
    return (
      <MDBox p={3}>
        <MDTypography variant="h5" fontWeight="medium" mb={2}>
          {title}
        </MDTypography>
        <MDTypography variant="body2" color="text">
          No hay datos disponibles
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox p={3}>
      <MDBox style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <MDTypography variant="h5" fontWeight="medium">
          {title}
        </MDTypography>
        <MDBox style={{ display: 'flex', gap: '8px' }}>
          <MDButton
            variant="outlined"
            color="success"
            size="small"
            startIcon={<FileExcel />}
            onClick={exportToExcel}
          >
            Excel
          </MDButton>
          <MDButton
            variant="outlined"
            color="info"
            size="small"
            startIcon={<Download />}
            onClick={handleDownloadPNG}
          >
            PNG
          </MDButton>
        </MDBox>
      </MDBox>

      {/* Filtros */}
      <MDBox style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <Autocomplete
          options={organismosUnicos}
          value={selectedOrganismo}
          onChange={(event, newValue) => setSelectedOrganismo(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Opciones de Filtro"
              variant="outlined"
              size="small"
            />
          )}
          style={{ minWidth: '250px' }}
          clearOnEscape
          disableClearable={false}
        />
      </MDBox>

      <MDBox 
        sx={{ 
          backgroundColor: 'white', 
          borderRadius: '8px',
          padding: '16px',
          boxShadow: '0 14px 28px rgba(0,0,0,0.22)',
          filter: 'drop-shadow(0 6px 14px rgba(0,0,0,0.18))'
        }}
        ref={chartRef}
      >
        <Plot
          data={plotData}
          layout={layout}
          config={config}
          style={{ width: '100%', height: '100%' }}
        />
      </MDBox>

      {observaciones && (
        <MDBox mt={2}>
          <MDTypography variant="caption" color="text">
            {observaciones}
          </MDTypography>
        </MDBox>
      )}

      {renderLegend()}

      <MDBox mt={2} p={2} style={{ backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
        <MDTypography variant="body2" color="text" fontWeight="medium">
          Total de registros mostrados: {filteredData.length}
        </MDTypography>
        <MDTypography variant="body2" color="text">
          Organismos mostrados: {organismosUnicos.length}
        </MDTypography>
        <MDTypography variant="body2" color="text">
          Suma total: {filteredData.reduce((sum, d) => sum + d.valor1, 0)}
        </MDTypography>
      </MDBox>
    </MDBox>
  );
};

export default BarChartStackedCustom;